import { useMemo } from 'react'
import type { AppDispatch } from '@redux/store'

import { multiFilteredObjectArraySelector } from '@domain-group/javascript-utils'
import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useComment from '@hooks/useComment'
import useCurrentUser from '@hooks/useCurrentUser'
import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as conversationActions from '@redux/modules/conversation'

import type { EntityOptions } from '@redux/modules/types'

import {
  CommentModel, ConversationModel, ConversationRequestOptions, UserModel,
} from '@models/types'

const watchEntityKeys = [
  'comments',
  'subscribers',
]

type CreateSubjectConversationParams = {
  entityParams: Partial<ConversationModel>,
  dispatch: AppDispatch,
  entityRequestOptions?: ConversationRequestOptions,
}

const createSubjectConversation = (params: CreateSubjectConversationParams) => {
  const { entityParams, dispatch, entityRequestOptions } = params
  const { createSubjectConversation: createFn } = conversationActions

  return dispatch(createFn(entityParams, entityRequestOptions))
}

type CreateCommentParams = {
  comment: Partial<CommentModel>,
  conversation: Partial<ConversationModel>,
  currentUser: Partial<UserModel>,
  createComment: (comment: CommentModel, entityRequestOptions: EntityOptions) => any,
  dispatch: AppDispatch,
  entityRequestOptions?: EntityOptions,
}

const createCommentWithConversation = (params: CreateCommentParams) => {
  const {
    comment, conversation, createComment, currentUser, dispatch, entityRequestOptions = {},
  } = params

  // If we have a conversation already we can just create a comment
  // If not we create a conversation for the subject with the first comment
  const updatedComment = {
    commentable_id: conversation.id,
    commentable_type: 'Conversation',
    ...comment,
  }

  // @ts-expect-error TS2345
  if (conversation.id) return createComment(updatedComment, entityRequestOptions)

  return createSubjectConversation({
    dispatch,
    entityParams: {
      ...conversation,
      // @ts-expect-error TS2322
      comment: updatedComment,
      subscribers: [{ user_id: currentUser.id }],
    },
    entityRequestOptions,
  })
}

export function useRelations(conversation: Partial<ConversationModel>) {
  const { campaign_id } = conversation

  const entities = useSelector(reduxState => reduxState.entities)
  const { campaigns } = entities

  const campaign = campaign_id && campaigns[campaign_id] ? campaigns[campaign_id] : {}

  return {
    campaign,
  }
}

type UseConversationOptions = {
  publicCommentsOnly?: boolean,
}

function useConversation(conversation: Partial<ConversationModel>, options: UseConversationOptions = {}) {
  const { publicCommentsOnly } = options || {}

  const dispatch = useDispatch()

  const {
    updatedEntities: {
      comments: commentsUpdatedAt,
      subscribers: subscribersUpdatedAt,
    },
  // @ts-expect-error TS2322
  } = useWatchEntityUpdates(watchEntityKeys, { customUseSelectorFn: useSelector })

  const entities = useSelector(reduxState => reduxState.entities)
  const { comments, images, users } = entities

  // @ts-expect-error TS2339
  const { agency, campaign } = useRelations(conversation)

  const { creating } = useSelector(reduxState => reduxState.conversations)

  const { callbacks: { createComment }, creating: creatingComment } = useComment({
    commentable_id: conversation.id,
    commentable_type: 'Conversation',
  })

  const { currentUser } = useCurrentUser()

  const filteredComments = useMemo(() => {
    const array = Object.values(comments)

    const filtered = array.filter((comment) => {
      const { commentable_id, commentable_type, public: isPublic } = comment

      const commentableIdMatch = commentable_id === conversation.id
      const commentableTypeMatch = commentable_type === 'Conversation'
      const publicMatch = publicCommentsOnly ? isPublic : true

      return commentableIdMatch && commentableTypeMatch && publicMatch
    })

    return filtered
  }, [conversation.id, commentsUpdatedAt, publicCommentsOnly])

  const filteredSubscribers = useMemo(
    () => multiFilteredObjectArraySelector({ entities }, 'subscribers', [
      // @ts-expect-error TS2322
      { key: 'subscribable_id', value: conversation.id },
      { key: 'subscribable_type', value: 'Conversation' },
    ]),
    [conversation.id, subscribersUpdatedAt],
  )

  const lastComment = filteredComments[filteredComments.length - 1] || {}
  const commentCount = filteredComments.length

  return {
    agency,
    campaign,
    callbacks: {
      createCommentWithConversation: (
        comment: Partial<CommentModel>,
        entityRequestOptions?: EntityOptions,
      ) => createCommentWithConversation({
        comment,
        conversation,
        createComment,
        currentUser,
        dispatch,
        entityRequestOptions,
      }),
      createSubjectConversation: (entityParams: Partial<ConversationModel>, entityRequestOptions: EntityOptions) => (
        createSubjectConversation({ entityParams, dispatch, entityRequestOptions })
      ),
    },
    commentCount,
    comments: filteredComments,
    conversation,
    creating: creating || creatingComment,
    entities: {
      images,
      users,
    },
    lastComment,
    subscribers: filteredSubscribers,
  }
}

export default useConversation
